/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { IDLE, LOADING, REJECTED, SUCCEEDED } from 'store/DataManagerConstants'
import { genericPromise2 } from 'utils/ActionUtils'

const INITIAL_STATE = {
    error: undefined,
    data: undefined,
    status: IDLE,
}

const useFetch = (url, options, fetchOptions) => {
    const cache = useRef({})

    // Used to prevent state update if the component is unmounted
    const cancelRequest = useRef(false)

    const {
        formatResult = r => r,
        shouldFetch = true,
    } = fetchOptions ?? {}

    // Keep state logic separated
    const fetchReducer = (state, action) => {
        switch (action.type) {
            case 'loading': return { ...INITIAL_STATE, status: LOADING, data: state.data }
            case 'fetched': return { ...INITIAL_STATE, status: SUCCEEDED, data: action.payload }
            case 'error': return { ...INITIAL_STATE, status: REJECTED, error: action.payload }
            case 'reset': return { ...INITIAL_STATE }
            default: return state
        }
    }

    const [state, dispatch] = useReducer(fetchReducer, INITIAL_STATE)

    const fetchData = async () => {
        cancelRequest.current = false
        dispatch({ type: 'loading' })

        try {
            const result = await genericPromise2(url, options)
                .then(formatResult)
                .catch(e => {
                    throw new Error(e)
                })

            cache.current[url] = result
            if (cancelRequest.current) {
                return
            }

            dispatch({ type: 'fetched', payload: result })
        } catch (error) {
            if (cancelRequest.current) {
                return
            }

            dispatch({ type: 'error', payload: error.message })
        }
    }

    useEffect(() => {
        // Do nothing if the url is not given
        if (!url || !shouldFetch) {
            return
        }

        // If a cache doesn't exists for this url, fetch data
        if (!cache.current[url]) {
            fetchData()
        }

        // Use the cleanup function for avoiding a possibly
        // state update after the component was unmounted
        return () => {
            cancelRequest.current = true
        }
    }, [url, shouldFetch])

    const reset = useCallback(() => dispatch({ type: 'reset' }), [dispatch])

    return {
        ...state,
        reload: fetchData,
        reset,
    }
}

export default useFetch